/**
 * Fragrance Finder Architecture
 * Senior Frontend Engine Implementation — Robust Version
 */

const NOTES_MAP = {
  'lemon': 'Citrus', 'bergamot': 'Citrus', 'orange': 'Citrus', 'mandarin': 'Citrus', 'lime': 'Citrus', 'grapefruit': 'Citrus',
  'rose': 'Floral', 'jasmine': 'Floral', 'lavender': 'Fresh', 'mint': 'Fresh', 'eucalyptus': 'Fresh',
  'amber': 'Oriental', 'vanilla': 'Sweet', 'caramel': 'Sweet', 'honey': 'Sweet',
  'musk': 'Musky', 'white musk': 'Musky',
  'cedarwood': 'Woody', 'sandalwood': 'Woody', 'patchouli': 'Woody', 'oud': 'Woody', 'vetiver': 'Woody'
};

const QUESTIONS = [
  { title: "Who is this fragrance for?", key: "gender", options: ["Male", "Female", "Unisex"] },
  { title: "When will you mostly wear it?", key: "occasion", options: ["Daily", "Office", "Party", "Wedding", "Date Night", "Gym"] },
  { title: "Which fragrance family do you prefer?", key: "family", options: ["Fresh", "Citrus", "Woody", "Floral", "Sweet", "Musky"] },
  { title: "How strong do you like your fragrance?", key: "strength", options: ["Light", "Moderate", "Strong"] },
  { title: "Which season do you wear it most?", key: "season", options: ["Summer", "Winter", "All Season"] },
  { title: "Which are your favourite notes?", key: "favouriteNotes", options: ["Citrus", "Vanilla", "Rose", "Oud", "Musk", "Aquatic"] }
];

const SCORING_WEIGHTS = {
  gender: 30,
  family: 25,
  favouriteNotes: 20,
  occasion: 15,
  season: 10,
  strength: 10
};

const INFERENCE_DICTIONARIES = {
  gender: {
    male: ['male', 'men', 'pour homme', 'man', 'him', 'masculine'],
    female: ['female', 'women', 'pour femme', 'woman', 'her', 'feminine'],
    unisex: ['unisex', 'shared', 'neutral', 'all']
  },
  season: {
    summer: ['summer', 'spring', 'warm', 'hot', 'beach', 'sunny'],
    winter: ['winter', 'autumn', 'fall', 'cold', 'snow', 'cozy'],
    'all season': ['all season', 'year round', 'anytime', 'versatile', 'everyday']
  },
  strength: {
    light: ['light', 'cologne', 'eau de cologne', 'edc', 'fresh', 'subtle', 'weak'],
    moderate: ['moderate', 'eau de toilette', 'edt', 'balanced', 'daytime'],
    strong: ['strong', 'intense', 'eau de parfum', 'edp', 'parfum', 'extrait', 'heavy', 'beast', 'projection']
  },
  occasion: {
    daily: ['daily', 'casual', 'everyday', 'relaxed'],
    office: ['office', 'work', 'professional', 'corporate', 'meeting'],
    party: ['party', 'club', 'nightlife', 'clubbing', 'loud'],
    wedding: ['wedding', 'formal', 'gala', 'black tie', 'elegant'],
    'date night': ['date', 'romance', 'romantic', 'evening', 'sensual', 'night'],
    gym: ['gym', 'sport', 'sporty', 'active', 'workout', 'fresh']
  }
};

const Quiz = {
  current: 0,
  answers: {},
  cachedProfiles: [],

  async init() {
    this.cacheDOM();
    this.bindEvents();
    await this.loadProducts();
  },

  cacheDOM() {
    this.heroScreen = document.getElementById("ff-hero");
    this.quizScreen = document.getElementById("ff-quiz");
    this.loadingScreen = document.getElementById("ff-loading");
    this.resultsScreen = document.getElementById("ff-result");
    
    this.questionContainer = document.getElementById("ff-question");
    this.currentText = document.getElementById("ff-current");
    this.totalText = document.getElementById("ff-total");
    this.progressBar = document.querySelector(".ff-progress-bar");
    
    this.startBtn = document.querySelector(".ff-start");
    this.prevBtn = document.getElementById("ff-prev");
    
    this.loadingSteps = document.querySelectorAll(".ff-loading-step");
    this.resultsContainer = document.getElementById("ff-results-container");

    if (this.totalText) {
      this.totalText.textContent = QUESTIONS.length;
    }
  },

  bindEvents() {
    if (this.startBtn) {
      this.startBtn.addEventListener("click", () => this.startQuiz());
    }
    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", () => this.previous());
    }
  },

  async loadProducts() {
    try {
      const response = await fetch('/collections/fragrance-finder/products.json?limit=250');
      if (!response.ok) throw new Error("Fault fetching configuration profiles.");
      const data = await response.json();
      
      this.cachedProfiles = (data.products || []).map(product => this.buildProductProfile(product));
    } catch (error) {
      console.error("[FragranceFinder Error]", error);
    }
  },

  extractNotes(htmlDescription) {
    const result = { top: [], heart: [], base: [] };
    if (!htmlDescription) return result;

    const tmp = document.createElement("div");
    tmp.innerHTML = htmlDescription;
    const cleanText = tmp.textContent || tmp.innerText || "";
    const lowerText = cleanText.replace(/\s+/g, " ").toLowerCase();

    const topMarkers = ["top notes:", "top:", "opening:"];
    const heartMarkers = ["heart notes:", "middle notes:", "heart:", "middle:"];
    const baseMarkers = ["base notes:", "base:", "dry down:"];
    const allMarkers = [...topMarkers, ...heartMarkers, ...baseMarkers];

    const getSegment = (currentMarkers, nextTargetMarkers) => {
      let startIndex = -1;
      let matchedMarker = "";

      for (const marker of currentMarkers) {
        const index = lowerText.indexOf(marker);
        if (index !== -1 && (startIndex === -1 || index < startIndex)) {
          startIndex = index;
          matchedMarker = marker;
        }
      }

      if (startIndex === -1) return [];

      let endIndex = lowerText.length;
      allMarkers.forEach(marker => {
        if (marker !== matchedMarker) {
          const foundIndex = lowerText.indexOf(marker, startIndex + matchedMarker.length);
          if (foundIndex !== -1 && foundIndex < endIndex) {
            endIndex = foundIndex;
          }
        }
      });

      const segmentText = lowerText.substring(startIndex + matchedMarker.length, endIndex);
      return segmentText
        .split(",")
        .map(n => n.trim())
        .filter(n => n.length > 0);
    };

    result.top = getSegment(topMarkers, [...heartMarkers, ...baseMarkers]);
    result.heart = getSegment(heartMarkers, baseMarkers);
    result.base = getSegment(baseMarkers, []);

    return result;
  },

  inferValue(field, searchString) {
    const dict = INFERENCE_DICTIONARIES[field];
    if (!dict) return "";

    for (const [key, tokens] of Object.entries(dict)) {
      for (const token of tokens) {
        if (searchString.includes(token)) {
          return key;
        }
      }
    }
    return "";
  },

  buildProductProfile(product) {
    const notes = this.extractNotes(product.body_html);
    const flattenedNotes = [...notes.top, ...notes.heart, ...notes.base];
    
    const families = new Set();
    flattenedNotes.forEach(note => {
      if (NOTES_MAP[note]) {
        families.add(NOTES_MAP[note]);
      }
    });

    const tags = Array.isArray(product.tags) ? product.tags : (typeof product.tags === 'string' ? product.tags.split(',') : []);
    const cleanTags = tags.map(t => t.trim().toLowerCase());

    const findTagValue = (prefix) => {
      const matchingTag = cleanTags.find(t => t.startsWith(`${prefix}-`));
      return matchingTag ? matchingTag.replace(`${prefix}-`, '').trim() : '';
    };

    let gender = findTagValue('gender');
    let season = findTagValue('season');
    let strength = findTagValue('strength');
    let occasion = findTagValue('occasion');

    const searchContext = `${product.title} ${product.vendor} ${product.body_html || ''} ${Array.from(families).join(' ')}`.toLowerCase();

    if (!gender) gender = this.inferValue('gender', searchContext) || 'unisex';
    if (!season) season = this.inferValue('season', searchContext) || 'all season';
    if (!strength) strength = this.inferValue('strength', searchContext) || 'moderate';
    if (!occasion) occasion = this.inferValue('occasion', searchContext) || 'daily';

    const getShortDescription = (html) => {
      if (!html) return "";
      const div = document.createElement("div");
      div.innerHTML = html;
      const text = div.textContent || div.innerText || "";
      
      let cutoff = text.length;
      const parsedMarkers = ["Top Notes:", "Top:", "Opening:", "Heart Notes:", "Middle Notes:", "Base Notes:", "Base:"];
      parsedMarkers.forEach(m => {
        const idx = text.indexOf(m);
        if (idx !== -1 && idx < cutoff) cutoff = idx;
      });

      return text.substring(0, cutoff).trim() || text.substring(0, 120).trim() + "...";
    };

    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      url: `/products/${product.handle}`,
      vendor: product.vendor,
      image: product.images && product.images[0] ? product.images[0].src : '',
      shortDescription: getShortDescription(product.body_html),
      notes: notes,
      families: Array.from(families),
      gender: gender,
      season: season,
      strength: strength,
      occasion: occasion
    };
  },

  startQuiz() {
    if (this.heroScreen) this.heroScreen.classList.add("ff-hidden");
    if (this.quizScreen) this.quizScreen.classList.remove("ff-hidden");
    this.current = 0;
    this.answers = {};
    this.renderQuestion();
  },

  renderQuestion() {
    const q = QUESTIONS[this.current];
    if (!q) return;

    if (this.currentText) this.currentText.textContent = this.current + 1;
    if (this.progressBar) {
      this.progressBar.style.width = `${((this.current + 1) / QUESTIONS.length) * 100}%`;
    }

    if (this.current === 0) {
      this.prevBtn?.classList.add("ff-hidden");
    } else {
      this.prevBtn?.classList.remove("ff-hidden");
    }

    let html = `<h2 class="ff-question-title">${q.title}</h2>`;
    html += `<div class="ff-options">`;
    
    q.options.forEach(option => {
      const isSelected = this.answers[q.key] === option ? "selected" : "";
      html += `
        <button class="ff-option ${isSelected}" data-value="${option}">
          ${option}
        </button>
      `;
    });
    html += `</div>`;

    if (this.questionContainer) {
      this.questionContainer.innerHTML = html;
      
      this.questionContainer.querySelectorAll(".ff-option").forEach(btn => {
        btn.addEventListener("click", (e) => this.handleOptionSelection(e));
      });
    }
  },

  handleOptionSelection(event) {
    const btn = event.currentTarget;
    const q = QUESTIONS[this.current];
    
    this.questionContainer.querySelectorAll(".ff-option").forEach(b => b.classList.remove("selected"));
    btn.classList.add("selected");
    
    this.answers[q.key] = btn.getAttribute("data-value");

    setTimeout(() => {
      this.next();
    }, 350);
  },

  next() {
    if (this.current < QUESTIONS.length - 1) {
      this.current++;
      this.renderQuestion();
    } else {
      this.renderLoading();
    }
  },

  previous() {
    if (this.current > 0) {
      this.current--;
      this.renderQuestion();
    }
  },

  calculateScore(profile, answers) {
    let score = 0;
    const reasons = [];

    const normalize = (val) => String(val || '').toLowerCase().trim();

    // 1. Gender Metric (30 pts)
    const targetGender = normalize(answers.gender);
    const profileGender = normalize(profile.gender);
    if (targetGender && profileGender) {
      if (profileGender === targetGender || profileGender === 'unisex') {
        score += SCORING_WEIGHTS.gender;
        reasons.push(`Matches your preference for ${answers.gender} formulations`);
      }
    }

    // 2. Favourite Family Metric (25 pts)
    const targetFamily = normalize(answers.family);
    if (targetFamily && profile.families) {
      let matchedFamilyScore = 0;
      profile.families.forEach(fam => {
        const f = normalize(fam);
        if (f === targetFamily) {
          matchedFamilyScore = SCORING_WEIGHTS.family;
        } else if (f.includes(targetFamily) || targetFamily.includes(f)) {
          matchedFamilyScore = Math.max(matchedFamilyScore, SCORING_WEIGHTS.family * 0.5);
        }
      });
      score += matchedFamilyScore;
      if (matchedFamilyScore === SCORING_WEIGHTS.family) {
        reasons.push(`Perfect match for your preferred ${answers.family} fragrance family`);
      } else if (matchedFamilyScore > 0) {
        reasons.push(`Strong partial match for your interest in the ${answers.family} dynamic family`);
      }
    }

    // 3. Favourite Notes Metric (20 pts)
    const targetFavNoteClass = normalize(answers.favouriteNotes);
    if (targetFavNoteClass && profile.notes) {
      const allNotesCombined = [...profile.notes.top, ...profile.notes.heart, ...profile.notes.base].map(normalize);
      
      let containsExactNote = allNotesCombined.some(n => n === targetFavNoteClass);
      let containsMappedNote = allNotesCombined.some(n => NOTES_MAP[n] && normalize(NOTES_MAP[n]) === targetFavNoteClass);

      if (containsExactNote || containsMappedNote) {
        score += SCORING_WEIGHTS.favouriteNotes;
        reasons.push(`Contains rich profiles matching your preferred ${answers.favouriteNotes} profiles`);
      }
    }

    // 4. Occasion Metric (15 pts)
    const targetOccasion = normalize(answers.occasion);
    const profileOccasion = normalize(profile.occasion);
    if (targetOccasion && profileOccasion) {
      if (profileOccasion === targetOccasion) {
        score += SCORING_WEIGHTS.occasion;
        reasons.push(`Optimized and curated beautifully for ${answers.occasion} wear`);
      } else if (profileOccasion.includes(targetOccasion) || targetOccasion.includes(profileOccasion)) {
        score += SCORING_WEIGHTS.occasion * 0.5;
        reasons.push(`Highly versatile and dynamic selection safe for ${answers.occasion} settings`);
      }
    }

    // 5. Season Metric (10 pts)
    const targetSeason = normalize(answers.season);
    const profileSeason = normalize(profile.season);
    if (targetSeason && profileSeason) {
      if (profileSeason === targetSeason || profileSeason === 'all season') {
        score += SCORING_WEIGHTS.season;
        reasons.push(`Perfect composition structural matrix for ${answers.season}`);
      }
    }

    // 6. Strength Metric (10 pts)
    const targetStrength = normalize(answers.strength);
    const profileStrength = normalize(profile.strength);
    if (targetStrength && profileStrength) {
      if (profileStrength === targetStrength) {
        score += SCORING_WEIGHTS.strength;
        reasons.push(`Delivers your requested ${answers.strength} presence & sillage configuration`);
      }
    }

    return { score, matchPercentage: Math.round((score / 110) * 100), reasons };
  },

  findMatches() {
    const scored = this.cachedProfiles.map(profile => {
      const evaluation = this.calculateScore(profile, this.answers);
      return { 
        profile, 
        score: evaluation.score, 
        matchPercentage: evaluation.matchPercentage, 
        reasons: evaluation.reasons 
      };
    });

    return scored
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  },

  async renderLoading() {
    if (this.quizScreen) this.quizScreen.classList.add("ff-hidden");
    if (this.loadingScreen) this.loadingScreen.classList.remove("ff-hidden");

    this.loadingSteps.forEach(step => {
      step.classList.remove("done");
      const icon = step.querySelector(".ff-step-icon");
      if (icon) icon.textContent = "";
    });

    const runStep = (index) => {
      return new Promise(resolve => {
        setTimeout(() => {
          if (this.loadingSteps[index]) {
            this.loadingSteps[index].classList.add("done");
            const icon = this.loadingSteps[index].querySelector(".ff-step-icon");
            if (icon) icon.textContent = "✓";
          }
          resolve();
        }, 900);
      });
    };

    for (let i = 0; i < this.loadingSteps.length; i++) {
      await runStep(i);
    }

    setTimeout(() => {
      this.renderResults();
    }, 300);
  },

  renderResults() {
    if (this.loadingScreen) this.loadingScreen.classList.add("ff-hidden");
    if (this.resultsScreen) this.resultsScreen.classList.remove("ff-hidden");

    const matches = this.findMatches();
    if (!matches || matches.length === 0) {
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = `<p class="ff-no-match">No products matched your parameters. Try restarting the profiling layout.</p>`;
      }
      return;
    }

    const primary = matches[0];
    const alternatives = matches.slice(1);

    let html = `
      <div class="ff-best-match-card">
        <div class="ff-badge">Best Match — ${primary.matchPercentage}% Alignment</div>
        <div class="ff-main-grid">
          ${primary.profile.image ? `
            <div class="ff-image-wrap">
              <img src="${primary.profile.image}" alt="${primary.profile.title}">
            </div>
          ` : ''}
          <div class="ff-details-wrap">
            <span class="ff-vendor">${primary.profile.vendor}</span>
            <h3 class="ff-product-name">${primary.profile.title}</h3>
            <p class="ff-description">${primary.profile.shortDescription}</p>
            
            ${primary.reasons.length > 0 ? `
              <div class="ff-recommendation-reasons">
                <h5>Why We Recommend This:</h5>
                <ul>
                  ${primary.reasons.map(reason => `<li>✔ ${reason}</li>`).join('')}
                </ul>
              </div>
            ` : ''}

            <div class="ff-meta-tags">
              ${primary.profile.families.map(fam => `<span class="ff-tag">${fam}</span>`).join('')}
            </div>
            <a href="${primary.profile.url}" class="ff-shop-now-btn">Shop Now</a>
          </div>
        </div>
      </div>
    `;

    if (alternatives.length > 0) {
      html += `
        <h4 class="ff-alt-heading">Alternative Discoveries</h4>
        <div class="ff-alternatives-grid">
      `;
      
      alternatives.forEach(alt => {
        html += `
          <div class="ff-alt-card">
            <div class="ff-alt-badge">${alt.matchPercentage}% Match</div>
            ${alt.profile.image ? `
              <div class="ff-alt-img">
                <img src="${alt.profile.image}" alt="${alt.profile.title}">
              </div>
            ` : ''}
            <span class="ff-alt-vendor">${alt.profile.vendor}</span>
            <h5 class="ff-alt-title">${alt.profile.title}</h5>
            
            ${alt.reasons.length > 0 ? `
              <div class="ff-recommendation-reasons ff-alt-reasons">
                <ul>
                  ${alt.reasons.slice(0, 2).map(reason => `<li>✔ ${reason}</li>`).join('')}
                </ul>
              </div>
            ` : ''}

            <a href="${alt.profile.url}" class="ff-alt-btn">Explore Now</a>
          </div>
        `;
      });
      
      html += `</div>`;
    }

    html += `
      <div class="ff-action-footer">
        <button class="ff-retry-btn" id="ff-retry-action">Restart Quiz</button>
      </div>
    `;

    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = html;
      const retryBtn = document.getElementById("ff-retry-action");
      if (retryBtn) {
        retryBtn.addEventListener("click", () => this.startQuiz());
      }
    }
  }
};

document.addEventListener("DOMContentLoaded", () => {
  Quiz.init();
});